import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Mail, Phone, Linkedin, Instagram, Facebook } from "lucide-react";
import Logo from "@/components/brand/Logo";
import StoreBadges from "@/components/brand/StoreBadges";
import LanguageSwitcher from "@/components/i18n/LanguageSwitcher";
import { useLocalePath } from "@/i18n/LanguageSync";
import { SITE } from "@/lib/site";

const GUIDE_URL = (process.env.REACT_APP_BACKEND_URL || "") + "/api/v1/integration-guide";
const SWAGGER_URL = (process.env.REACT_APP_BACKEND_URL || "") + "/docs";

export default function Footer() {
  const { t } = useTranslation();
  const lp = useLocalePath();
  return (
    <footer
      data-testid="site-footer"
      className="border-t border-slate-200/70 bg-[#F7F9FC]"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-20">
        <div className="grid grid-cols-2 md:grid-cols-12 gap-10">
          <div className="col-span-2 md:col-span-4">
            <Logo />
            <p className="mt-5 text-[14px] leading-relaxed text-slate-600 max-w-sm">
              {t("footer.tagline")}
            </p>

            <div className="mt-7 space-y-3 text-[14px]">
              <p className="text-xs uppercase tracking-[0.18em] text-slate-500">
                Get the apps
              </p>
              <div>
                <p className="text-[12px] font-semibold text-slate-500 mb-2">
                  Safe to Spend 365
                </p>
                <StoreBadges product="safe-to-spend-footer" />
              </div>
              <div className="mt-4">
                <p className="text-[12px] font-semibold text-slate-500 mb-2">
                  Ember360
                </p>
                <StoreBadges product="ember-footer" />
              </div>
            </div>
          </div>

          <div className="md:col-span-2">
            <p className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-4">
              {t("footer.products")}
            </p>
            <ul className="space-y-2.5 text-[14px] text-slate-700">
              <li>
                <Link to={lp("/safe-to-spend")} data-testid="footer-link-safe-to-spend" className="hover:text-[#0A1128]">
                  Safe to Spend 365
                </Link>
              </li>
              <li>
                <Link to={lp("/ember")} data-testid="footer-link-ember" className="hover:text-[#0A1128]">
                  Ember360
                </Link>
              </li>
            </ul>

            <p className="text-xs uppercase tracking-[0.18em] text-slate-500 mt-8 mb-4">
              {t("footer.resources")}
            </p>
            <ul className="space-y-2.5 text-[14px] text-slate-700">
              <li>
                <Link to={lp("/platform")} data-testid="footer-link-apis" className="hover:text-[#0A1128]">
                  {t("nav.apis")}
                </Link>
              </li>
              <li>
                <Link to={lp("/developers")} data-testid="footer-link-docs" className="hover:text-[#0A1128]">
                  {t("footer.developerDocs")}
                </Link>
              </li>
              <li>
                <Link to={lp("/status")} data-testid="footer-link-status" className="hover:text-[#0A1128]">
                  {t("footer.apiStatus")}
                </Link>
              </li>
              <li>
                <Link to={lp("/changelog")} data-testid="footer-link-changelog" className="hover:text-[#0A1128]">
                  {t("footer.changelog")}
                </Link>
              </li>
              <li>
                <Link to={lp("/blog")} data-testid="footer-link-blog" className="hover:text-[#0A1128]">
                  {t("nav.blog")}
                </Link>
              </li>
              <li>
                <Link to={lp("/press")} data-testid="footer-link-press" className="hover:text-[#0A1128]">
                  {t("footer.press")}
                </Link>
              </li>
              <li>
                <Link to="/portal/login" data-testid="footer-link-partner-portal" className="hover:text-[#0A1128]">
                  {t("nav.partnerPortal")}
                </Link>
              </li>
            </ul>

            <p className="text-xs uppercase tracking-[0.18em] text-slate-500 mt-8 mb-4">
              For Developers
            </p>
            <ul className="space-y-2.5 text-[14px] text-slate-700">
              <li>
                <a
                  href={GUIDE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-link-api-docs"
                  className="hover:text-[#1A4232]"
                >
                  Integration Guide
                </a>
              </li>
              <li>
                <a
                  href={SWAGGER_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-link-swagger"
                  className="hover:text-[#1A4232]"
                >
                  Swagger / OpenAPI
                </a>
              </li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <p className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-4">
              For Business
            </p>
            <ul className="space-y-2.5 text-[14px] text-slate-700">
              <li>
                <Link
                  to="/for-employers"
                  data-testid="footer-link-for-employers"
                  className="hover:text-[#0A1128]"
                >
                  For Employers
                </Link>
              </li>
              <li>
                <Link
                  to="/for-employers#pricing"
                  data-testid="footer-link-for-employers-pricing"
                  className="hover:text-[#0A1128]"
                >
                  Package Pricing
                </Link>
              </li>
              <li>
                <Link
                  to="/for-employers/inquiry"
                  data-testid="footer-link-for-employers-demo"
                  className="hover:text-[#0A1128]"
                >
                  Request a Demo
                </Link>
              </li>
              <li>
                <a
                  href={`mailto:${SITE.email}?subject=Corporate%20sales%20inquiry`}
                  data-testid="footer-link-for-employers-sales"
                  className="hover:text-[#0A1128]"
                >
                  Contact Sales
                </a>
              </li>
            </ul>

            <p className="text-xs uppercase tracking-[0.18em] text-slate-500 mt-8 mb-4">
              {t("footer.company")}
            </p>
            <ul className="space-y-2.5 text-[14px] text-slate-700">
              <li>
                <Link to={lp("/about")} data-testid="footer-link-about" className="hover:text-[#0A1128]">
                  {t("footer.aboutUs")}
                </Link>
              </li>
              <li>
                <Link to={lp("/contact")} data-testid="footer-link-contact" className="hover:text-[#0A1128]">
                  {t("nav.contact")}
                </Link>
              </li>
            </ul>

            <p className="text-xs uppercase tracking-[0.18em] text-slate-500 mt-8 mb-4">
              {t("footer.legal")}
            </p>
            <ul className="space-y-2.5 text-[14px] text-slate-700">
              <li><Link to={lp("/legal/terms")} data-testid="footer-link-terms" className="hover:text-[#0A1128]">{t("footer.terms")}</Link></li>
              <li><Link to={lp("/legal/privacy")} data-testid="footer-link-privacy" className="hover:text-[#0A1128]">{t("footer.privacy")}</Link></li>
              <li><Link to={lp("/legal/security")} data-testid="footer-link-security" className="hover:text-[#0A1128]">{t("footer.security")}</Link></li>
              <li><Link to={lp("/legal/cookies")} data-testid="footer-link-cookies" className="hover:text-[#0A1128]">{t("footer.cookies")}</Link></li>
              <li><Link to={lp("/legal/acceptable-use")} data-testid="footer-link-aup" className="hover:text-[#0A1128]">{t("footer.acceptableUse")}</Link></li>
              <li><Link to={lp("/legal/api-terms")} data-testid="footer-link-api-terms" className="hover:text-[#0A1128]">{t("footer.apiTerms")}</Link></li>
              <li><Link to={lp("/legal/dpa")} data-testid="footer-link-dpa" className="hover:text-[#0A1128]">{t("footer.dpa")}</Link></li>
              <li><Link to={lp("/sla")} data-testid="footer-link-sla" className="hover:text-[#0A1128]">{t("footer.sla")}</Link></li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <p className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-4">
              {t("footer.support")}
            </p>
            <ul className="space-y-3 text-[14px] text-slate-700">
              <li className="flex items-start gap-2.5">
                <Mail className="h-4 w-4 mt-0.5 text-slate-500 shrink-0" />
                <a
                  href={`mailto:${SITE.email}`}
                  data-testid="footer-email"
                  className="hover:text-[#0A1128] text-[12.5px] leading-snug whitespace-nowrap"
                >
                  {SITE.email}
                </a>
              </li>
              <li className="flex items-start gap-2.5">
                <Phone className="h-4 w-4 mt-0.5 text-slate-500" />
                <a
                  href={`tel:${SITE.phone.replace(/[^0-9]/g, "")}`}
                  data-testid="footer-phone"
                  className="hover:text-[#0A1128]"
                >
                  {SITE.phone}
                </a>
              </li>
            </ul>

            <p className="text-xs uppercase tracking-[0.18em] text-slate-500 mt-8 mb-4">
              Privacy
            </p>
            <ul className="space-y-2.5 text-[14px] text-slate-700">
              <li>
                <Link
                  to="/delete-account"
                  data-testid="footer-link-delete-account"
                  className="hover:text-[#0A1128]"
                >
                  Delete account
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-slate-200 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="text-[13px] text-slate-500">
            {t("footer.copyright", { year: new Date().getFullYear() })}
          </p>
          <div className="flex items-center gap-3" data-testid="footer-socials">
            <a
              href={SITE.socials.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="AtlasForge Financial on LinkedIn"
              data-testid="footer-social-linkedin"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 hover:text-[#0A1128] hover:border-[#0A1128] transition-colors"
            >
              <Linkedin className="h-4 w-4" />
            </a>
            <a
              href={SITE.socials.instagram}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="AtlasForge Financial on Instagram"
              data-testid="footer-social-instagram"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 hover:text-[#0A1128] hover:border-[#0A1128] transition-colors"
            >
              <Instagram className="h-4 w-4" />
            </a>
            <a
              href={SITE.socials.facebook}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="AtlasForge Financial on Facebook"
              data-testid="footer-social-facebook"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 hover:text-[#0A1128] hover:border-[#0A1128] transition-colors"
            >
              <Facebook className="h-4 w-4" />
            </a>
            <a
              href={SITE.socials.tiktok}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Safe to Spend 365 on TikTok"
              data-testid="footer-social-tiktok"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 hover:text-[#0A1128] hover:border-[#0A1128] transition-colors"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
                <path d="M16.6 5.82s.51.5 0 0A4.28 4.28 0 0 1 15.54 3h-3.09v12.4a2.59 2.59 0 0 1-2.59 2.5c-1.42 0-2.6-1.16-2.6-2.6c0-1.72 1.66-3.01 3.37-2.48V9.66c-3.45-.46-6.47 2.22-6.47 5.64c0 3.33 2.76 5.7 5.69 5.7c3.14 0 5.69-2.55 5.69-5.7V9.01a7.35 7.35 0 0 0 4.3 1.38V7.3s-1.88.09-3.24-1.48Z" />
              </svg>
            </a>
          </div>
          <p className="text-[12px] text-slate-500 max-w-2xl">
            AtlasForge Financial is a technology company. Banking services are provided by partner institutions. Investing involves risk.
          </p>
        </div>
      </div>
    </footer>
  );
}
